import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  SETTING_KEYS,
  useSaveGeneralSetting,
} from "../hooks/useGeneralSetting";
import { findSettingByType } from "../helpers/helpers";
import {
  LANGUAGE_CHOSEN_EVENT,
  normalizeLanguage,
  saveLanguage,
} from "./languages";

/**
 * Carries the FR | EN choice with the account, like useThemeSync does for the theme
 * (general-setting/set-setting, key "language"):
 * - on sign-in, a language saved on the account wins and is remembered on this device;
 * - pressing FR or EN while signed in saves the choice to the account.
 * Nothing is sent when the person never chose (the browser's language stays a guess).
 * The account's language is its owner's: a helper user's choice (a user with a parent)
 * stays on their device until the setting can be saved per user.
 * Until the backend accepts the "language" key the save fails silently and the
 * choice stays on the device.
 */
export const useLanguageSync = () => {
  const user = useSelector((state) => state.user);
  const { i18n } = useTranslation();
  const { save } = useSaveGeneralSetting();

  const isOwner = !user?.parent_id;
  const saveRef = useRef(save);
  saveRef.current = save;
  const adopted = useRef(false);

  useEffect(() => {
    if (!user?.token) {
      adopted.current = false;
      return;
    }
    if (adopted.current) return;
    adopted.current = true;
    const stored = normalizeLanguage(
      findSettingByType(user?.general_settings, SETTING_KEYS.language)?.value,
    );
    if (!stored || !isOwner) return;
    saveLanguage(stored, { announce: false });
    if (normalizeLanguage(i18n.resolvedLanguage || i18n.language) !== stored) {
      i18n.changeLanguage(stored);
    }
  }, [user, i18n, isOwner]);

  const token = user?.token;
  useEffect(() => {
    if (!token || !isOwner) return undefined;
    const onChosen = (event) => {
      saveRef.current(SETTING_KEYS.language, event.detail, { notify: false });
    };
    window.addEventListener(LANGUAGE_CHOSEN_EVENT, onChosen);
    return () => window.removeEventListener(LANGUAGE_CHOSEN_EVENT, onChosen);
  }, [token, isOwner]);
};
